import { SearchBar, Select, type Column } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  crmPackageMappings,
  type CrmPackageMapping,
} from "../../api/crm-package-mappings.service.js";
import { DataTable } from "../../components/DataTable/DataTable.js";
import { PaginationControls } from "../../components/PaginationControls/PaginationControls.js";
import LoadingState from "../../components/LoadingState/index.js";
import EmptyState from "../../components/EmptyState/index.js";
import { formatDateShort } from "../../utils/dateFormatter.js";
import styles from "./CrmPackageMappings.module.css";

type CrmPackageMappingListContext = {
  refetchTrigger?: number;
  setListError?: (error: Error | null) => void;
};

function CrmPackageMappingList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext<CrmPackageMappingListContext>();
  const [displayedMappings, setDisplayedMappings] = useState<CrmPackageMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const searchTerm = searchParams.get("search") || "";
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }

  function handleSearchChange(value: string) {
    setCurrentPage(1);
    updateURL({ search: value, page: "1" });
  }

  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const pageSizeFromURL = Number(searchParams.get("pageSize")) || 10;
    if (pageFromURL !== currentPage) setCurrentPage(pageFromURL);
    if (pageSizeFromURL !== itemsPerPage) setItemsPerPage(pageSizeFromURL);
  }, [searchParams]);

  useEffect(() => {
    loadMappings();
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    if (context.refetchTrigger !== undefined && context.refetchTrigger > 0) {
      loadMappings();
    }
  }, [context.refetchTrigger]);

  async function loadMappings() {
    try {
      setLoading(true);
      setError(null);
      if (context.setListError) {
        context.setListError(null);
      }
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await crmPackageMappings.getAll({
        search: searchTerm || undefined,
        limit: itemsPerPage,
        offset: offset,
      });
      setDisplayedMappings(response.mappings);
      setTotal(response.total);
    } catch (err) {
      const loadError = err instanceof Error ? err : new Error("Failed to load mappings");
      setError(loadError);
      if (context.setListError) {
        context.setListError(loadError);
      }
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;
  const startIndex = offset;
  const endIndex = offset + displayedMappings.length;

  function handleMappingClick(mapping: CrmPackageMapping) {
    navigate(`/fulfilment/crm-package-mappings/${mapping.id}`);
  }

  const columns: Column<CrmPackageMapping>[] = [
    {
      header: "CRM Package ID",
      accessor: "crmPackageId",
      render: (_value, row) => (
        <span style={{ fontFamily: "monospace", fontSize: "0.875rem" }}>{row.crmPackageId}</span>
      ),
    },
    {
      header: "Action",
      accessor: "action",
      render: (_value, row) => (
        <span
          className={`${styles.actionBadge} ${row.action === "skip" ? styles.actionSkip : styles.actionMap}`}
        >
          {row.action}
        </span>
      ),
    },
    {
      header: "Package",
      accessor: "packageId",
      render: (_value, row) =>
        row.package ? (
          <span style={{ fontWeight: 500 }}>
            {row.package.name} ({row.package.productCode})
          </span>
        ) : (
          <span style={{ color: "var(--color-text-secondary)", fontStyle: "italic" }}>—</span>
        ),
    },
    {
      header: "Description",
      accessor: "description",
      render: (_value, row) => (
        <span style={{ color: row.description ? "inherit" : "var(--color-text-secondary)" }}>
          {row.description || "No description"}
        </span>
      ),
    },
    {
      header: "Created",
      accessor: "createdAt",
      render: (_value, row) => (row.createdAt ? formatDateShort(row.createdAt) : "-"),
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (_value, row) => (row.updatedAt ? formatDateShort(row.updatedAt) : "-"),
    },
  ];

  if (loading && displayedMappings.length === 0) {
    return <LoadingState message="Loading mappings..." />;
  }

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load mappings"
        message={error.message}
        action={{
          label: "Retry",
          onClick: loadMappings,
        }}
      />
    );
  }

  return (
    <>
      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by CRM Package ID or Product Code..."
          onClear={() => handleSearchChange("")}
        />
      </div>

      <div className={styles.controlsSection}>
        <div />
        <div className={styles.pageSizeSelector}>
          <Select
            label="Items per page"
            value={itemsPerPage}
            onChange={e => {
              const newSize = Number(e.target.value);
              setItemsPerPage(newSize);
              setCurrentPage(1);
              updateURL({ pageSize: String(newSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      <div className={styles.contentSection}>
        {displayedMappings.length === 0 ? (
          <EmptyState
            variant="empty"
            title="No mappings found"
            message={
              searchTerm
                ? "Try adjusting your search criteria"
                : "Create your first CRM package mapping to get started"
            }
          />
        ) : (
          <DataTable
            columns={columns}
            data={displayedMappings}
            onRowClick={handleMappingClick}
            rowKey="id"
          />
        )}

        {!loading && !error && displayedMappings.length > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={total}
            itemName="mappings"
            onPageChange={page => {
              setCurrentPage(page);
              updateURL({ page: String(page) });
            }}
          />
        )}
      </div>
    </>
  );
}

export default CrmPackageMappingList;
